import { useState, useMemo } from "react";

import { ListBox, ListBoxItem, type Selection } from "react-aria-components";

import { CheckboxIndicator } from "#components/Checkbox";
import { TextField } from "#components/TextField";
import { cn } from "#utils";

import type { CheckboxColumn } from "./types";

export interface CheckboxColumnProps {
  column: CheckboxColumn;
  onSelect: (value: string) => void;
  selectedValue?: string;
  onSearch?: (searchTerm: string) => void;
  searchTerm?: string;
}

export function CheckboxColumn({
  column,
  onSelect,
  selectedValue,
  onSearch,
  searchTerm = "",
}: CheckboxColumnProps) {
  const [internalSelectedValue, setInternalSelectedValue] = useState<string>(selectedValue || "");

  // Parse selected values from comma-separated string
  const selectedIds = useMemo(() => {
    return internalSelectedValue ? internalSelectedValue.split(",") : [];
  }, [internalSelectedValue]);

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!searchTerm) {
      return column.items;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return column.items.filter((item) => item.name.toLowerCase().includes(lowerSearchTerm));
  }, [column.items, searchTerm]);

  const handleSelectionChange = (selection: Selection) => {
    if (selection === "all") return;
    const values = Array.from(selection) as string[];
    setInternalSelectedValue(values.join(","));
    if (values.length > 0) onSelect(values.join(","));
  };

  // Handle search value change
  const handleSearchChange = (value: string) => {
    onSearch?.(value);
  };

  return (
    <>
      <TextField
        aria-label={`${column.title} search`}
        className="w-full gap-0 px-2 py-1"
        inputProps={{
          placeholder: column.placeholder,
          className: "h-[1.625rem] text-body-small",
        }}
        onChange={handleSearchChange}
        value={searchTerm}
      />

      <ListBox
        aria-label={`${column.title} options`}
        onSelectionChange={handleSelectionChange}
        selectedKeys={selectedIds}
        selectionMode="multiple"
      >
        {filteredItems.map((item) => (
          <ListBoxItem
            className={({ isHovered, isFocusVisible, isSelected }) =>
              cn(
                "flex cursor-pointer gap-2 border-r px-lg py-xs text-body-small focus:outline-none",
                isHovered || isFocusVisible || isSelected
                  ? "border-outline-brand bg-overlay-brand"
                  : "border-overlay-medium",
              )
            }
            id={item.id}
            key={item.id}
            textValue={item.name}
          >
            {({ isSelected, isHovered, isFocusVisible }) => (
              <>
                <CheckboxIndicator
                  isFocused={isFocusVisible}
                  isHovered={isHovered}
                  isSelected={isSelected}
                />
                <div className="overflow-hidden text-ellipsis whitespace-nowrap">{item.name}</div>
              </>
            )}
          </ListBoxItem>
        ))}
      </ListBox>
    </>
  );
}
